import { Constants } from 'app/app.constants';
// Angular
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
// RxJS
import { filter, mergeMap, tap, withLatestFrom, switchMap } from 'rxjs/operators';
import { defer, of, timer } from 'rxjs';
// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
// Auth actions
import { AuthActionTypes, Login, Logout, Register, UserLoaded, UserRequested } from '../_actions/auth.actions';
import { AuthService } from '../_services/index';
import { AppState } from '../../reducers';
import { environment } from '../../../../environments/environment';
import { isUserLoaded } from '../_selectors/auth.selectors';
import { CookiesService } from '../_services/cookies.service';

@Injectable()
export class AuthEffects {
    login$ = createEffect(() => this.actions$.pipe(
        ofType<Login>(AuthActionTypes.Login),
        tap(action => {
            localStorage.setItem(environment.authTokenKey, action.payload.authToken);
            this.store.dispatch(new UserRequested());
        }),
    ), { dispatch: false });

    logout$ = createEffect(() => this.actions$.pipe(
        ofType<Logout>(AuthActionTypes.Logout),
        tap(() => {
            localStorage.removeItem(environment.authTokenKey);
            this.cookiesService.deleteCookie('_remCred_');
            localStorage.removeItem('user');
            localStorage.removeItem('username');
            localStorage.removeItem('fullname');
            this.router.navigate(['/auth/login'], { queryParams: { returnUrl: this.returnUrl } });
        })
    ), { dispatch: false });

    register$ = createEffect(() => this.actions$.pipe(
        ofType<Register>(AuthActionTypes.Register),
        tap(action => {
            localStorage.setItem(environment.authTokenKey, action.payload.authToken);
        })
    ), { dispatch: false });

    loadUser$ = createEffect(() => this.actions$
        .pipe(
            ofType<UserRequested>(AuthActionTypes.UserRequested),
            withLatestFrom(this.store.pipe(select(isUserLoaded))),
            filter(([action, _isUserLoaded]) => !_isUserLoaded),
            mergeMap(([action, _isUserLoaded]) => this.auth.getUserByToken()),
            tap(_user => {
                if (_user) {
                    localStorage.setItem('user', _user.username);
                    localStorage.setItem('username', _user.username);
                    // Single source: fullname for all modules (CnC, etc.). Fallback to username when API does not return fullname (e.g. dev).
                    localStorage.setItem('fullname', (_user.fullname && String(_user.fullname).trim() !== '') ? String(_user.fullname).trim() : (_user.username || ''));
                    this.store.dispatch(new UserLoaded({ user: _user }));
                } else {
                    this.store.dispatch(new Logout());
                }
            })
        ), { dispatch: false });

    init$ = createEffect(() => defer(() => {
        const userToken = localStorage.getItem(environment.authTokenKey);
        let observableResult = of({ type: 'NO_ACTION' });
        if (userToken) {
            observableResult = of(new Login({ authToken: userToken }));
        }
        return observableResult;
    }));

    // Timeout time in minutes
    //TIME_IN_MINS = 1;

    // Timeout time converted
    APPLICATION_TIMEOUT_TIME = 1000 * 60 * Constants.TIME_IN_MINS;

    extendApplicationTimeout$ = createEffect(() => this.actions$.pipe(
        switchMap((_action: Action) => {
            const userToken = localStorage.getItem(environment.authTokenKey);
            if (userToken) {
                return timer(this.APPLICATION_TIMEOUT_TIME);
            }
            return of(null);
        }),
        tap(() => {
            const userToken = localStorage.getItem(environment.authTokenKey);
            if (userToken) {
                this.store.dispatch(new Logout());
            }
        })
    ), { dispatch: false });


    private returnUrl: string;

    constructor(
        private actions$: Actions,
        private router: Router,
        private auth: AuthService,
        private store: Store<AppState>,
        private cookiesService: CookiesService
    ) {

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.returnUrl = event.url;
            }
        });
    }
}
